import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { login as apiLogin, register as apiRegister, logout as apiLogout, verifyTwoStep, type TwoStepChallenge } from '@/api/auth';
import type { LoginCredentials, RegisterData } from '@/types';

export function useAuth() {
  const navigate = useNavigate();
  const hydrate = useAuthStore((s) => s.hydrate);
  const clearAuth = useAuthStore((s) => s.logout);
  const { isAuthenticated, plan, role, tenantId, tenantSlug, email } = useAuthStore();

  const enter = useCallback(async () => {
    const ok = await hydrate(); // populate identity from /auth/me
    if (!ok) throw new Error('Login succeeded but session could not be established');
    navigate('/dashboard');
  }, [hydrate, navigate]);

  /** Returns the two-step challenge when the account needs a code; otherwise signs in. */
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<TwoStepChallenge | null> => {
      const challenge = await apiLogin(credentials); // sets cookies unless two-step is on
      if (challenge) return challenge;
      await enter();
      return null;
    },
    [enter]
  );

  /** The second step: the code (or a backup code) for a challenge from login. */
  const finishTwoStep = useCallback(
    async (challenge: string, code: string) => {
      const result = await verifyTwoStep(challenge, code); // sets cookies
      await enter();
      return result;
    },
    [enter]
  );

  const register = useCallback(
    async (data: RegisterData) => {
      await apiRegister(data); // sets cookies
      const ok = await hydrate();
      if (!ok) throw new Error('Registration succeeded but session could not be established');
      navigate('/dashboard');
    },
    [hydrate, navigate]
  );

  const logout = useCallback(async () => {
    await apiLogout();
    clearAuth();
    navigate('/login');
  }, [clearAuth, navigate]);

  return {
    login,
    finishTwoStep,
    register,
    logout,
    isAuthenticated,
    plan,
    role,
    tenantId,
    tenantSlug,
    email,
  };
}
